import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Bell, Sparkle, SignOut, CaretDown, List, Sun, Moon, Monitor } from "@phosphor-icons/react";
import { useTheme } from "../context/ThemeContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { roleLabel } from "../lib/constants";
import OrgSwitcher from "./OrgSwitcher";
import ActiveProject from "./ActiveProject";

const TITLES = [
  ["/pricing", "Plan, Usage & Pricing"],
  ["/dashboard", "Dashboard"], ["/projects", "Projects"], ["/clients", "Clients"],
  ["/design-engine", "Design Engine"], ["/validation", "Validation"],
  ["/drawing-studio", "Drawing Studio"], ["/documents", "Documents"], ["/bom", "BOM Generator"],
  ["/scm", "SCM & Vendors"], ["/admin", "Admin"], ["/analytics", "Analytics"],
  ["/equipment", "Equipment Catalog"],
  ["/industry-packs", "Industry Packs"],
];

const TAB_TITLES = {
  overview: "Project", specs: "Specifications", intake: "Intake", design: "Design Engine",
  validation: "Validation", documents: "Documents", drawings: "Drawing Studio",
  bom: "BOM", scm: "SCM & Vendors", revisions: "Revisions", activity: "Activity",
};

export default function Header({ copilotOpen, onToggleCopilot, onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { mode, resolved, setMode } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const cycleTheme = () => {
    const order = { system: "dark", dark: "light", light: "system" };
    setMode(order[mode] || "dark");
  };

  const onProject = loc.pathname.startsWith("/projects/") && loc.pathname !== "/projects/new";
  const title = onProject
    ? TAB_TITLES[(new URLSearchParams(loc.search).get("tab") || "overview").toLowerCase()] || "Project"
    : TITLES.find(([p]) => loc.pathname.startsWith(p))?.[1] || "Formigen";

  const loadNotes = () =>
    api.get("/notifications").then(({ data }) => {
      setNotes(data.notifications || []);
      setUnread(data.unread || 0);
      return data;
    }).catch(() => null);

  useEffect(() => {
    loadNotes();
    const iv = setInterval(loadNotes, 30000);
    return () => clearInterval(iv);
  }, [loc.pathname]);

  const openNotes = async (v) => {
    setOpen(v);
    if (!v) return;
    const fresh = await loadNotes();
    if ((fresh ? fresh.unread : unread) > 0) {
      await api.post("/notifications/read-all").catch(() => {});
      setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
      setUnread(0);
    }
  };

  const clickNote = async (n) => {
    if (!n.read) {
      api.patch(`/notifications/${n.id}/read`).catch(() => {});
      setNotes((ns) => ns.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.link) {
      setOpen(false);
      nav(n.link);
    }
  };

  return (
    <header className="relative h-14 shrink-0 bg-[var(--c-surface)] border-b border-[var(--c-border)] flex items-center gap-3 px-5 z-30" data-testid="header">
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px ember-rule opacity-50" />
      <button onClick={onToggleSidebar} data-testid="sidebar-toggle"
        className="lg:hidden p-1.5 rounded-sm text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] transition-colors">
        <List size={18} />
      </button>
      <h1 className="text-base font-semibold tracking-tight text-[var(--c-text)] font-heading truncate" data-testid="header-title">{title}</h1>
      <div className="flex-1" />
      <ActiveProject />
      <OrgSwitcher />
      <button
        data-testid="theme-toggle"
        onClick={cycleTheme}
        title={mode === "system" ? `Theme: follows OS (${resolved}) — click for dark` : mode === "dark" ? "Theme: dark — click for light" : "Theme: light — click for system"}
        className="p-2 rounded-sm text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] transition-colors duration-150"
      >
        {mode === "system" ? <Monitor size={17} /> : resolved === "dark" ? <Moon size={17} /> : <Sun size={17} />}
      </button>
      <button
        data-testid="copilot-toggle-btn"
        onClick={onToggleCopilot}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors duration-150 ${
          copilotOpen ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:border-[var(--c-muted)]"
        }`}
      >
        <Sparkle size={14} weight={copilotOpen ? "fill" : "regular"} />
        AI Copilot
      </button>
      <Popover open={open} onOpenChange={openNotes}>
        <PopoverTrigger asChild>
          <button data-testid="notification-bell" className="relative p-2 rounded-sm text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] transition-colors duration-150">
            <Bell size={18} weight={unread > 0 ? "fill" : "regular"} />
            {unread > 0 && (
              <span data-testid="notification-count"
                className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-0.5 rounded-full bg-[var(--c-red)] text-white text-[9px] font-mono flex items-center justify-center">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 bg-[var(--c-surface)] border-[var(--c-border)]" align="end" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-[var(--c-border)] text-xs font-mono tracking-widest uppercase text-[var(--c-muted)]">Notifications</div>
          <div className="max-h-80 overflow-y-auto">
            {notes.length === 0 && <div className="px-4 py-6 text-sm text-[var(--c-muted)]">No notifications</div>}
            {notes.map((n) => (
              <button key={n.id} onClick={() => clickNote(n)}
                className={`w-full text-left px-4 py-3 border-b border-[var(--c-border)]/60 last:border-0 transition-colors hover:bg-[var(--c-surface-2)]/70 ${n.read ? "opacity-60" : ""}`}
                data-testid={`notification-${n.kind}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--c-accent)] shrink-0" />}
                  <div className="min-w-0">
                    {n.title && <div className="text-[12px] font-medium text-[var(--c-text)] truncate">{n.title}</div>}
                    <div className="text-[11px] text-[var(--c-muted)] leading-snug">{n.text}</div>
                    <div className="text-[9px] font-mono text-[var(--c-muted)]/70 mt-1 uppercase tracking-wider">
                      {(n.kind || "").replace(/_/g, " ")} · {(n.created_at || "").slice(5, 16).replace("T", " ")}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-2 pr-1 py-1.5 rounded-sm hover:bg-[var(--c-surface-2)] transition-colors duration-150">
            <div className="w-7 h-7 rounded-sm bg-[var(--c-accent)]/20 border border-[var(--c-accent)]/40 flex items-center justify-center text-xs font-semibold text-[var(--c-text)]">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="text-left hidden md:block">
              <div className="text-xs font-medium text-[var(--c-text)] leading-tight">{user?.name}</div>
              <div className="text-[10px] text-[var(--c-muted)] leading-tight">{roleLabel(user?.role)}</div>
            </div>
            <CaretDown size={12} className="text-[var(--c-muted)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[var(--c-surface)] border-[var(--c-border)] w-48" align="end">
          <DropdownMenuItem
            data-testid="logout-btn"
            className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)] focus:text-[var(--c-text)] cursor-pointer"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <SignOut size={14} className="mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
